import { Eye, EyeClosed } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router";

import appleLogo from "@/assets/apple-logo.svg";
import googleLogo from "@/assets/google-logo.svg";
import { AuthDesktopLeftPane } from "@/components/auth/AuthDesktopLeftPane";
import { AuthDesktopRightPane } from "@/components/auth/AuthDesktopRightPane";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TextInputField } from "@/components/ui/TextInputField";
import { isAppleSignInAvailable } from "@/lib/platform";
import { consumeRedirectAfterAuth } from "@/lib/redirectAfterAuth";
import { useAuthStore } from "@/store/authStore";

export default function LoginDesktop() {
  const { user, error, signIn, signInWithGoogle, signInWithApple, clearError } =
    useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const redirect = consumeRedirectAfterAuth();
    return <Navigate to={redirect ?? "/"} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await signIn(email, password);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-[#F6F6F9]">
      <AuthDesktopLeftPane />

      <AuthDesktopRightPane title="Sign-up or Login to Continue">
        <form
          onSubmit={handleSubmit}
          className="space-y-4 lg:space-y-5 xl:space-y-6"
        >
          <TextInputField
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter your Email"
            autoComplete="email"
            required
            inputClassName="rounded-lg border-[#d4d7e3] bg-white px-3.5 text-sm placeholder:text-[#626f95] lg:h-11 lg:px-4 lg:text-base xl:h-12"
          />

          <div className="space-y-1.5 lg:space-y-2">
            <TextInputField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter Password"
              minLength={8}
              required
              inputClassName="rounded-lg border-[#d4d7e3] bg-white text-sm placeholder:text-[#626f95] lg:h-11 lg:text-base xl:h-12"
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="rounded p-1 text-black/70 hover:bg-black/5"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeClosed className="h-4 w-4" />
                  )}
                </button>
              }
            />
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-muted-foreground hover:text-foreground text-xs transition"
              >
                Forgot Password?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            variant="vibrant-mobile"
            size="lg-mobile"
            disabled={submitting}
            className="h-10 w-full text-sm lg:h-11 xl:h-12"
          >
            {submitting ? "Signing in..." : "Continue"}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-[#626f95] lg:mt-6 xl:mt-7">
          or
        </p>

        <div className="mt-4 flex justify-center gap-4 lg:mt-5 lg:gap-5 xl:gap-6">
          {isAppleSignInAvailable() ? (
            <Button
              type="button"
              variant="outline"
              className="h-11 w-11 rounded-lg p-2 lg:h-12 lg:w-12 xl:h-14 xl:w-14 xl:p-2.5"
              onClick={() => void signInWithApple()}
            >
              <img
                src={appleLogo}
                alt="Apple"
                className="h-4 w-4 object-contain lg:h-[18px] lg:w-[18px] xl:h-5 xl:w-5"
              />
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="h-11 w-11 rounded-lg p-2 lg:h-12 lg:w-12 xl:h-14 xl:w-14 xl:p-2.5"
            onClick={() => void signInWithGoogle()}
          >
            <img
              src={googleLogo}
              alt="Google"
              className="h-4 w-4 object-contain lg:h-[18px] lg:w-[18px] xl:h-5 xl:w-5"
            />
          </Button>
        </div>

        {error ? (
          <div className="mt-5 flex justify-center lg:mt-6 xl:mt-7">
            <Alert className="w-full max-w-80 border-[#d4d7e3] bg-white py-3 text-[#ef4444]">
              <AlertDescription className="text-sm leading-5 font-medium text-[#ef4444]">
                {error}
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        <div className="mt-6 text-center lg:mt-7 xl:mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#e5e7ef]" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-[#F6F6F9] px-2 text-[#626f95]">
                New to Mash?
              </span>
            </div>
          </div>
          <div className="mt-3 lg:mt-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#212330] transition hover:text-black"
            >
              Have an invite code? Sign up here
            </Link>
          </div>
        </div>

        <div className="mt-8 text-center lg:mt-10 xl:mt-12 2xl:mt-16">
          <p className="text-xs tracking-wide text-[#626f95]">
            Disclaimer message about reading and accepting{" "}
            <span className="font-semibold text-[#212330]">
              Terms of Service
            </span>{" "}
            &{" "}
            <span className="font-semibold text-[#212330]">Privacy Policy</span>
          </p>
        </div>
      </AuthDesktopRightPane>
    </div>
  );
}
